import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useBackupHistory, useBackupStatus, useRequestBackup } from '@/hooks/useAdmin';
import { useTranslation } from '@/i18n/useTranslation';
import { EmptyState } from '@/components/common/EmptyState';
import { InlineError } from '@/components/common/InlineError';
import { TableSkeleton } from '@/components/common/LoadingSkeleton';
import { Pagination } from '@/components/common/Pagination';

const PAGE_SIZE = 10;

export function DatabaseBackupPanel() {
  const { displayValue, locale, t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [jobId, setJobId] = useState<string | null>(null);
  const history = useBackupHistory({ page, page_size: PAGE_SIZE });
  const requestBackup = useRequestBackup();
  const status = useBackupStatus(jobId);
  const job = status.data;
  const currentStatus = job?.status;
  const currentJobId = job?.id;
  const isActive = (state?: string) => state === 'pending' || state === 'running';
  const hasActiveJob =
    isActive(job?.status) || history.data?.items.some((item) => isActive(item.status));

  useEffect(() => {
    if (currentStatus && ['completed', 'failed'].includes(currentStatus)) {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'backups', 'history'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    }
  }, [currentJobId, currentStatus, queryClient]);

  return (
    <section className="glass-panel stack" aria-labelledby="database-backup-heading">
      <div className="cluster" style={{ justifyContent: 'space-between' }}>
        <h2 id="database-backup-heading">{t('Database backups')}</h2>
        <button
          className="btn btn--primary"
          type="button"
          disabled={
            requestBackup.isPending || !history.data || history.isError || Boolean(hasActiveJob)
          }
          onClick={() =>
            requestBackup.mutate(undefined, {
              onSuccess: (created) => {
                setJobId(created.id);
                setPage(1);
              },
            })
          }
        >
          {requestBackup.isPending ? t('Queuing backup…') : t('Run backup now')}
        </button>
      </div>
      <p>
        {t(
          'Create a full database backup in the private db-backup container. Queued jobs are not completed backups.',
        )}
      </p>
      <Link to="/admin/audit-log?entity_type=backup_job">{t('View backup audit log')}</Link>
      {requestBackup.isError && <InlineError error={requestBackup.error} />}
      {status.isError && <InlineError error={status.error} onRetry={() => status.refetch()} />}
      {job && (
        <div role="status" aria-live="polite">
          {job.status === 'pending' && t('Backup queued. Waiting for a worker.')}
          {job.status === 'running' && t('Backup in progress. Uploading may take several minutes.')}
          {job.status === 'completed' && t('Backup completed and uploaded to db-backup.')}
          {job.status === 'failed' && t('Backup failed. See the error and audit log.')}
          <p className="font-mono">{job.id}</p>
        </div>
      )}
      {job?.status === 'failed' && job.error_message && (
        <InlineError error={new Error(job.error_message)} />
      )}
      {history.isLoading && <TableSkeleton rows={PAGE_SIZE} cols={6} />}
      {history.isError && <InlineError error={history.error} onRetry={() => history.refetch()} />}
      {history.data?.items.length === 0 && <EmptyState title={t('No backup jobs yet')} />}
      {history.data && history.data.items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <caption className="visually-hidden">{t('Backup history')}</caption>
              <thead>
                <tr>
                  <th>{t('Job ID')}</th>
                  <th>{t('Trigger')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Requested at')}</th>
                  <th>{t('Finished at')}</th>
                  <th>{t('Size / error')}</th>
                </tr>
              </thead>
              <tbody>
                {history.data.items.map((item) => (
                  <tr key={item.id}>
                    <td className="font-mono">{item.id}</td>
                    <td>{displayValue(item.trigger)}</td>
                    <td>
                      {item.status === 'completed'
                        ? t('Backup completed')
                        : displayValue(item.status)}
                    </td>
                    <td>{new Date(item.created_at).toLocaleString(locale)}</td>
                    <td>
                      {item.completed_at ? new Date(item.completed_at).toLocaleString(locale) : '—'}
                    </td>
                    <td>
                      {item.error_message ||
                        (item.file_size_bytes !== null
                          ? t('{count} bytes', {
                              count: item.file_size_bytes.toLocaleString(locale),
                            })
                          : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={history.data.total}
            onPageChange={setPage}
          />
        </>
      )}
    </section>
  );
}
